/** Auth through {@link AuthOutboundHttpPort} (e.g. core `HttpClient`). */
import type { AuthProviderPort } from '../../domain/ports/AuthProviderPort.ts';
import type { AuthTokenStoragePort } from '../../domain/ports/AuthTokenStoragePort.ts';
import type { AuthHttpPostOptions, AuthOutboundHttpPort } from '../../domain/ports/AuthOutboundHttpPort.ts';

const SESSION_FETCH: AuthHttpPostOptions = {
  credentials: 'include',
  treat401AsPlainError: true,
  retries: 0,
};

export class ApiAuthAdapter implements AuthProviderPort {
  /** Constructs the API auth adapter. */
  constructor(
    private readonly http: AuthOutboundHttpPort,
    private readonly tokenStorage: AuthTokenStoragePort | null = null
  ) {}
  /** Authenticates a user via the API. */
  async login(usernameOrEmail: string, password: string): Promise<{ accessToken: string; user?: unknown }> {
    const data = await this.http.post<{ accessToken: string; user?: unknown }>(
      '/auth/login',
      { email: usernameOrEmail, password },
      SESSION_FETCH,
    );

    if (!data.accessToken) {
      throw new Error('No access token received from server');
    }

    return {
      accessToken: data.accessToken,
      user: data.user ?? null,
    };
  }

  /** Logs out the current user via the API. */
  async logout(): Promise<void> {
    if (this.tokenStorage) {
      this.tokenStorage.clearToken();
    }

    try {
      await this.http.post('/auth/logout', undefined, SESSION_FETCH);
    } catch {
      /* No guarantee: remote logout is optional. */
    }
  }

  /** Retrieves the current session. */
  async getSession(): Promise<{ accessToken: string | null; user?: unknown }> {
    const token = this.tokenStorage?.getToken() || null;
    return { accessToken: token, user: null };
  }

  /** Checks if the user is authenticated. */
  async isAuthenticated(): Promise<boolean> {
    const token = this.tokenStorage?.getToken() || null;
    return !!token;
  }
  
  /** Retrieves the current user. */
  async getUser(): Promise<unknown> {
    return null;
  }
}
